import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { DEFAULT_USER } from '../../constants';
import { isEmailDuplicate, hasUserWarning, generateKey } from '../../utils';
import type { PreviewUser, ValidationError, UserRole } from '../../types';

interface UsersTabProps {
  users: PreviewUser[];
  onChange: (users: PreviewUser[]) => void;
  errors: ValidationError[];
  disabled?: boolean;
}

export function UsersTab({
  users,
  onChange,
  errors,
  disabled = false,
}: UsersTabProps) {
  const { t } = useTranslation('customer-onboarding');

  const handleAdd = () => {
    onChange([...users, { ...DEFAULT_USER, _key: generateKey() }]);
  };

  const handleRemove = (index: number) => {
    onChange(users.filter((_, i) => i !== index));
  };

  const handleChange = (
    index: number,
    field: keyof PreviewUser,
    value: string | boolean
  ) => {
    const updated = [...users];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const getRowErrors = (rowIndex: number) => {
    return errors.filter(
      (e) => e.entity === 'users' && e.row === rowIndex + 1
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t('users.count', { count: users.length })}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          disabled={disabled}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('users.addUser')}
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">#</TableHead>
              <TableHead className="min-w-[200px]">
                {t('users.columns.email')} <span className="text-destructive">*</span>
              </TableHead>
              <TableHead className="min-w-[120px]">{t('users.columns.firstName')}</TableHead>
              <TableHead className="min-w-[120px]">{t('users.columns.lastName')}</TableHead>
              <TableHead className="min-w-[140px]">{t('users.columns.role')}</TableHead>
              <TableHead className="min-w-[120px]">{t('users.columns.department')}</TableHead>
              <TableHead className="min-w-[120px]">{t('users.columns.cellPhone')}</TableHead>
              <TableHead className="w-[80px] text-center">{t('users.columns.alertContact')}</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="h-24 text-center">
                  {t('users.noUsers')}
                </TableCell>
              </TableRow>
            ) : (
              users.map((user, index) => {
                const rowErrors = getRowErrors(index);
                const isDuplicate = isEmailDuplicate(user.email, users, index);
                const hasWarning = hasUserWarning(user);

                return (
                  <TableRow
                    key={user._key}
                    className={cn(
                      rowErrors.length > 0 && 'bg-destructive/5',
                      isDuplicate && 'bg-amber-50',
                      hasWarning && !isDuplicate && 'bg-amber-50/50'
                    )}
                  >
                    <TableCell className="font-medium text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="email"
                        value={user.email}
                        onChange={(e) =>
                          handleChange(index, 'email', e.target.value)
                        }
                        disabled={disabled}
                        placeholder={t('users.placeholders.email')}
                        className={cn(
                          'h-8',
                          isDuplicate && 'border-amber-500 focus-visible:ring-amber-500'
                        )}
                      />
                      {isDuplicate && (
                        <p className="mt-1 text-xs text-amber-600">
                          {t('users.duplicateEmail')}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input
                        value={user.firstName}
                        onChange={(e) =>
                          handleChange(index, 'firstName', e.target.value)
                        }
                        disabled={disabled}
                        placeholder={t('users.placeholders.firstName')}
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={user.lastName}
                        onChange={(e) =>
                          handleChange(index, 'lastName', e.target.value)
                        }
                        disabled={disabled}
                        placeholder={t('users.placeholders.lastName')}
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={user.role}
                        onValueChange={(value) =>
                          handleChange(index, 'role', value as UserRole)
                        }
                        disabled={disabled}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full admin">{t('users.roles.fullAdmin')}</SelectItem>
                          <SelectItem value="group admin">{t('users.roles.groupAdmin')}</SelectItem>
                          <SelectItem value="user">{t('users.roles.user')}</SelectItem>
                          <SelectItem value="read only">{t('users.roles.readOnly')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        value={user.department}
                        onChange={(e) =>
                          handleChange(index, 'department', e.target.value)
                        }
                        disabled={disabled}
                        placeholder={t('users.placeholders.department')}
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="tel"
                        value={user.cellPhone}
                        onChange={(e) =>
                          handleChange(index, 'cellPhone', e.target.value)
                        }
                        disabled={disabled}
                        placeholder={t('users.placeholders.cellPhone')}
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Checkbox
                        checked={user.isAlertContact}
                        onCheckedChange={(checked) =>
                          handleChange(index, 'isAlertContact', !!checked)
                        }
                        disabled={disabled}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemove(index)}
                        disabled={disabled}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
